// File: src/hooks/useAuthToken.js
//
// Returns a getAccessTokenSilently-SHAPED function regardless of which
// identity provider is active, so any existing api.js call site
// (`someAPI.foo(getAccessTokenSilently)`) keeps working unchanged.
//
// Why this exists: MyProgress.jsx / MyPayments.jsx / MyAccount.jsx are
// shared by two populations — students who signed up before the Cognito
// migration (still on Auth0) and students using the new OTP flow (Cognito).
// Both need to hit the SAME backend endpoints from the SAME page
// components; only the token source differs. This hook is the one place
// that decision is made.
//
// apiRequest() in services/api.js calls this as:
//   await getAccessTokenSilently({ authorizationParams: { audience } })
// and uses the resolved string directly as the Bearer token — the Cognito
// branch below ignores that options argument (no audience/scope
// negotiation needed for a single-client user pool) and returns the same
// shape: a function resolving to a plain token string.

import { useAuth0 } from '@auth0/auth0-react';
import { useStudentAuth } from '../context/StudentAuthContext';
import { getStudentToken } from '../services/cognitoAuth';

export function useAuthToken() {
  const { getAccessTokenSilently } = useAuth0();
  const { isStudentAuthenticated } = useStudentAuth();

  return isStudentAuthenticated ? getStudentToken : getAccessTokenSilently;
}
